// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { apiIssueHandover } from "../../lib/api.js";
import { CREATION_TICKET_KEY } from "../../lib/constants.js";
import { t as ct } from "../../lib/content.js";
import { IS_NATIVE } from "../../lib/origin.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// The install explainer card itself (Aug 2026). Renders null whenever install isn't
// genuinely offerable on this device right now — already installed/standalone, or a
// browser that never fired beforeinstallprompt and isn't iOS — per the same no-dead-end
// rule as the notification flow's inline InstallButton: never render copy promising a
// button that can't work. The button is the shared InstallButton (wide variant), so the
// Android native-prompt path and the iOS Share → Add to Home Screen path (including the
// pre-household ?resume-invite=1 carry) live in exactly one place.
// The install sheet (Alan, 15 Sep — replaces the inline explainer card). A bottom sheet over
// the pitch, shown once on first visit in a browser that can actually install, never when
// already running standalone (useInstallPrompt's `installable` covers both). Dismissal is
// remembered per device, as before. It is NOT repeated after the household is created: a
// person who continues in the browser finds the offer again under Settings → Household, where
// it carries a handover ticket so installing later is seamless (see worker/lib/handover.js).
//
// Three benefits, Alan's words. "Works offline" is a real claim: the service worker precaches
// the shell and W1.7 caches the last household read, so the app opens and reads without a
// network; writes wait. Verified before it went into the copy.
// Exported so the CMS screen list can name it as a screen root (it is the "Install sheet").
// `code` and `adultInvite` added 20 Sep. This card started life pre-household, on the pitch
// screen, where there was nothing to carry — so its InstallButton was mounted bare. It is now
// ALSO raised after an adult accepts an invite (Root, after B4), and there a bare button takes
// the pre-household branch and carries `resume-invite=1`: a creation pass, for a device that
// has just joined a household and does not need one. The icon then launches with no household
// and no proof, which is the sign-in that never happens (Alan, 20 Sep).
//
// Both are passed straight through. Bare stays valid and still means pre-household, which is
// what the pitch screen wants.
export function InstallExplainerCard({ onDismiss, code = null, adultInvite = null }) {
  const { installable } = useInstallPrompt();
  if (!installable) return null;
  const benefit = (title, sub) => (
    <div className="flex items-start gap-2.5 py-2 border-t border-slate-100">
      <span className="text-emerald-600 font-extrabold flex-shrink-0">✓</span>
      <div><div className="text-[13px] font-bold text-slate-900 leading-tight">{title}</div><div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{sub}</div></div>
    </div>
  );
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 bg-white rounded-t-2xl shadow-[0_-8px_30px_rgba(30,42,74,0.18)] px-5 pt-3 pb-6" role="dialog" aria-labelledby="install-sheet-title" data-testid="install-sheet">
      <div className="w-9 h-1 rounded bg-slate-200 mx-auto mb-3" aria-hidden="true" />
      <div className="w-12 h-12 rounded-xl bg-slate-900 text-white font-extrabold text-lg flex items-center justify-center mx-auto mb-2.5 shadow-md" aria-hidden="true">LB</div>
      <div id="install-sheet-title" className="text-lg font-extrabold text-slate-900 text-center leading-tight">{ct("onboarding.install.heading", "Install as an app")}</div>
      <p className="text-xs text-slate-500 text-center mt-1 mb-3">{ct("onboarding.install.hint", "Ten seconds, and you get the best of Little Bankers.")}</p>
      {benefit(ct("onboarding.install.b1", "Quick access, works offline"), ct("onboarding.install.b1_sub", "No tabs to keep open, and it works while you're on the go."))}
      {benefit(ct("onboarding.install.b2", "Alerts from your kids"), ct("onboarding.install.b2_sub", "Requests and reminders reach you, so you stay in control."))}
      {benefit(ct("onboarding.install.b3", "The best experience"), ct("onboarding.install.b3_sub", "The app is optimised to deliver you the best possible experience."))}
      <div className="border-t border-slate-100 pt-3.5 mt-1"><InstallButton variant="wide" code={code} adultInvite={adultInvite} label={ct("onboarding.install.cta", "Install as an app")} /></div>
      <button type="button" className="block w-full text-center text-xs font-semibold text-slate-500 underline mt-3" onClick={onDismiss} data-testid="install-sheet-dismiss">
        {ct("onboarding.install.dismiss", "Continue with browser for now")}
      </button>
    </div>
  );
}

/* ------------------------------ install button ---------------------------- */

// Shared install-availability state — used by both the header's InstallButton and
// SetupCompleteOverlay (Wizard.jsx), which needs to know WHETHER install is offerable
// here (not just render the button) so it can show sensible fallback copy instead of
// silently promising a button that never appears. `beforeinstallprompt` doesn't fire on
// every browser (desktop Firefox, in-app browsers, etc.) and won't fire twice once
// dismissed in some Chromium builds, so "not installable right now" is a real, common
// state — not just "already installed".
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => {
    try { return localStorage.getItem("lb_installed") === "1"; } catch { return false; }
  });
  const isIos = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  const isIosChrome = isIos && /crios/.test(navigator.userAgent.toLowerCase());
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;

  useEffect(() => {
    const promptHandler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    // Fires the moment Android finishes installing — lets us hide the button
    // right in the same tab, without waiting for the app to be reopened.
    const installedHandler = () => {
      try { localStorage.setItem("lb_installed", "1"); } catch {}
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", promptHandler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", promptHandler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  // Native Run A: the app IS the install. Every offer to add it to a home screen — the
  // header button, the sheet, the Settings card, the invite step, the notification prompts'
  // install-first branch — renders nothing in the Capacitor build.
  const installable = !IS_NATIVE && !isStandalone && !installed && (!!deferredPrompt || isIos);
  return { deferredPrompt, setDeferredPrompt, installed, setInstalled, isIos, isIosChrome, isStandalone, installable };
}



// The Add to Home Screen instruction. Lives outside InstallButton because after the install
// reload (LB-294) the button is not mounted — Root shows this at the top level on install=1.

// The URL the Install button reloads into (LB-294). Pure, so the exact shape is testable
// without a navigable window: the code, the ticket if one was issued, and install=1 so the
// fresh page shows the instruction. Pre-household carries the invite pass instead.
// LB-184: pre-household, the carry is the creation TICKET when this device holds one, so the
// installed app can create the household the invite paid for; "1" (pass only) when it does not,
// which is also what an icon installed before this change carries.
export function installReloadUrl(current, { code, ticket, invite, creationTicket, adultInvite } = {}) {
  const url = new URL(current);
  // Always "/", with only the carry params: the worker's install-time index.html route matches
  // exactly that, and the app lives at "/" anyway (every pushState passes an empty URL).
  url.pathname = "/"; url.search = ""; url.hash = "";
  // LB-342 (I1/I2): an adult part-way through accepting an emailed invite. Checked FIRST
  // because such a device holds no household and no device token — the `code` branch below
  // would be wrong for it, and the `invite` branch carries a creation pass it does not have.
  //
  // The carry is the invite link's own two parameters, in the shape Root's boot parser
  // already reads (`code` + `adultinvite`), so the installed app resumes the accept itself
  // rather than being handed a session the standalone storage jar never receives. That jar is
  // the whole reason this exists: on iOS a Home-Screen launch gets its own store, so anything
  // the browser tab set up — PIN, device identity, session — does not follow it. Installing
  // BEFORE the PIN means none of that has been created yet, so there is nothing to lose.
  if (adultInvite && adultInvite.code && adultInvite.token) {
    url.searchParams.set("code", adultInvite.code);
    url.searchParams.set("adultinvite", adultInvite.token);
    // Which step to come back to. Without it the installed app restarts the accept at the
    // household screen and asks the person to tap Join a second time, having already tapped
    // it in the browser a moment earlier (Alan, 20 Sep). It picks a step and nothing more:
    // the invite token is still the only thing that grants anything, and an unknown or
    // absent value simply starts at the beginning.
    if (adultInvite.step) url.searchParams.set("invitestep", adultInvite.step);
  }
  else if (code) { url.searchParams.set("resume", code); if (ticket) url.searchParams.set("t", ticket); }
  else if (invite) url.searchParams.set("resume-invite", creationTicket && /^[A-Za-z0-9_-]{16,128}$/.test(creationTicket) ? creationTicket : "1");
  url.searchParams.set("install", "1");
  return url.toString();
}

export function InstallInstructionModal({ open, onClose }) {
  const [dismissed, setDismissed] = useState(false);
  const isIosChrome = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) && /crios/.test(navigator.userAgent.toLowerCase());
  if (!open || dismissed) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="install-instruction">
      <div className="bg-white rounded-2xl p-5 max-w-xs text-center shadow-2xl">
        <div className="font-bold text-slate-800 mb-2">{ct("onboarding.iosmodal.heading", "Add Little Bankers to your Home Screen")}</div>
        <p className="text-xs text-slate-500">
          {isIosChrome
            ? <>In Chrome, tap the Share icon at the top right of the address bar, then "Add to Home Screen".</>
            : <>Tap the Share button in Safari (the square with an arrow), then "Add to Home Screen".</>}
        </p>
        <button onClick={() => { setDismissed(true); if (onClose) onClose(); }} className="mt-4 text-slate-600 font-semibold text-sm">
          Got it
        </button>
      </div>
    </div>
  );
}

export function InstallButton({ code, adultInvite = null, variant, className = "", label }) {
  const { deferredPrompt, setDeferredPrompt, setInstalled, isIos, isIosChrome, installable } = useInstallPrompt();
  const [showIosModal, setShowIosModal] = useState(false);
  const wide = variant === "wide"; // full-width CTA styling for the install explainer card
  // Compact icon-only styling (Aug 2026): matches the header's own ❓/⚙️ button chrome
  // exactly (same rounded-full/border/shadow treatment, no responsive size bump) so it
  // sits next to them as a natural third icon in that row on narrow screens — see the
  // header's own comment for why this needed to exist at all (the full-text button
  // wouldn't fit in that row without pushing the mode toggle off or overlapping the logo).
  const icon = variant === "icon";

  if (!installable) return null;

  async function handleClick() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      // appinstalled is the authoritative signal, but it can lag slightly — hide
      // right away too once the user has accepted, so the button doesn't linger.
      if (choice.outcome === "accepted") {
        try { localStorage.setItem("lb_installed", "1"); } catch {}
        setInstalled(true);
      }
    } else if (isIos) {
      // BUG FIX: iOS has no programmatic install API — the person has to do Share → Add to
      // Home Screen themselves in Safari's own UI, and whatever URL is showing in the
      // address bar at that moment becomes the new Home Screen icon's launch URL. That
      // matters because standalone (Home-Screen-launched) WebKit gives every app its own
      // separate storage sandbox from the Safari tab it was installed from, even for the
      // exact same origin — so the household code this device just wrote to localStorage
      // moments earlier in the wizard does NOT carry over. The icon launches into a fresh,
      // empty store with no household on it, which is why this used to dump people straight
      // back on the invitation gate right after finishing setup.
      //
      // Baking the code into the URL now, right before showing the instructions, means it
      // rides along as part of the URL Safari captures — and Root.jsx's `inviteOk`
      // initializer picks it back up on that first standalone launch, writes it into the
      // fresh store, and strips it from the address bar immediately after.
      // LB-342 (I1): an adult mid-accept. Nothing has been created on this device yet — no
      // household, no token, no PIN — so there is no handover to issue and nothing to lose.
      // The invite's own two parameters ride along and the installed app finishes the accept.
      if (adultInvite && adultInvite.code && adultInvite.token) {
        try {
          window.location.assign(installReloadUrl(window.location.href, { adultInvite }));
          return;
        } catch {
          // Non-fatal — they carry on in the browser and the offer returns in Settings.
        }
      } else if (code) {
        try {
          // Handover (Sep 2026): the code alone only told the installed app WHICH household;
          // it launched as an unknown device — no token, no image cookie, and locked out once
          // enforcement flips. A ticket carries proof, so the first launch is minted properly
          // and nothing has to be typed. Issued only if this device already holds a token,
          // which after a create or join it does; null falls back to the code-only carry.
          const ticket = await apiIssueHandover(code);
          // The runtime-edit carry failed with today's bundle live, which leaves load-time
          // capture as the only explanation standing (Claude Code eliminated the stale-bundle
          // one; Alan saw the failure). So RELOAD into the carried URL: the
          // worker serves index.html with the manifest link already parameterised, and
          // install=1 tells the fresh page to show the instruction. Root strips all three
          // params on boot, keeps the code, and — since this device holds a token — never
          // consumes the ticket, so it is still live for the icon's first launch.
          window.location.assign(installReloadUrl(window.location.href, { code, ticket }));
          return;
        } catch {
          // Non-fatal — worst case the URL doesn't carry the code and this device falls
          // back to the ordinary join screen after installing, same as before this fix.
        }
      } else {
        // Pre-household install (the pitch screen's install explainer): no household
        // code exists to carry yet, but the invitation-gate pass ALSO lives only in
        // Safari's localStorage and won't cross into the installed app's sandbox — carry
        // THAT instead, so the icon's first launch lands on the pitch screen rather than
        // demanding the invite code a second time. Root.jsx's inviteOk initializer
        // consumes and strips it, exactly like `resume` above.
        try {
          let creationTicket = null;
          try { creationTicket = localStorage.getItem(CREATION_TICKET_KEY); } catch {}
          window.location.assign(installReloadUrl(window.location.href, { invite: true, creationTicket })); // same reason as above
          return;
        } catch {
          // Non-fatal — worst case this device re-enters the invite code after install.
        }
      }
      setShowIosModal(true);
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        {...(icon ? { "aria-label": "Install app", title: "Install app" } : {})}
        className={`${wide
          ? "w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5"
          : icon
          ? "bg-pill text-navy rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap flex-shrink-0"
          : "bg-pill text-navy rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap flex-shrink-0"} ${className}`}
      >
        {wide ? (
          label || "Install the app"
        ) : icon ? (
          <Icon id="ic-install" size={18} style={{ color: "currentColor" }} />
        ) : (
          <>
            {/* R item 5 (Alan, Aug 2026): "Install" was hidden below the sm breakpoint —
                exactly the screens this button matters most on, since installing IS a
                mobile action. An icon-only 📲 wasn't self-explanatory on its own. */}
            <Icon id="ic-install" size={16} className="inline-block align-[-3px] mr-1" style={{ color: "currentColor" }} />Install
          </>
        )}
      </button>
      <InstallInstructionModal open={showIosModal} onClose={() => setShowIosModal(false)} />
    </>
  );
}
